import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Box, Chip, IconButton, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface Props {
  open: boolean;
  onClose: () => void;
}

const Section: React.FC<{ title: string; defaultExpanded?: boolean; children: React.ReactNode }> =
  ({ title, defaultExpanded, children }) => (
  <Accordion defaultExpanded={defaultExpanded} disableGutters elevation={0} square
    sx={{ '&:before': { display: 'none' }, borderBottom: 1, borderColor: 'divider' }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0 }}>
      <Typography variant="body2" component="div" sx={{ color: 'text.secondary', fontSize: '0.82rem', lineHeight: 1.65 }}>
        {children}
      </Typography>
    </AccordionDetails>
  </Accordion>
);

const K: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="code" sx={{
    fontFamily: 'monospace', fontSize: '0.76rem',
    bgcolor: 'action.hover', px: 0.5, py: 0.1, borderRadius: 0.5,
  }}>{children}</Box>
);

const ChartsHelpDialog: React.FC<Props> = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0.5 }}>
      Charts — User & Technical Guide
      <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
    </DialogTitle>
    <DialogContent dividers sx={{ maxHeight: '78vh', px: 0 }}>

      <Section title="Data Sources" defaultExpanded>
        <strong>Zerodha (Kite)</strong> — NSE equities, BSE equities, NFO/BFO derivatives, MCX/NCO commodities, CDS. Requires
        an active Zerodha session (see <em>Settings → Zerodha</em>).<br />
        <strong>Delta Exchange</strong> — 24×7 crypto futures (BTCUSD, ETHUSD, etc.). Requires Delta API credentials.<br />
        Each candle request picks the right provider automatically by the instrument&apos;s <K>exchange</K> field.
      </Section>

      <Section title="Candle Pipeline — how bars are populated">
        Each chart pane makes a live REST call to <K>/charts/candles</K> every time the symbol, interval, or lookback window changes.
        <br /><br />
        <strong>Zerodha path.</strong> The backend looks up the instrument token, translates the requested interval
        to Zerodha&apos;s native code (<em>1minute / 3minute / 5minute / 10minute / 15minute / 30minute / 60minute / day</em>),
        and calls <K>GET /instruments/historical/:token/:interval</K>. The ISO datetime (<K>+05:30</K>) from Zerodha
        is shifted to IST-aligned UNIX seconds (<K>+19800</K>) so the X-axis displays India time naturally.
        <br /><br />
        <strong>Delta path.</strong> The backend calls Delta REST with UTC unix seconds for <K>from</K>/<K>to</K>.
        Raw OHLCV is returned as-is (Delta is 24×7, no aggregation offset).
        <br /><br />
        <strong>Lookback sizing.</strong> The frontend sizes each pane&apos;s <K>from</K> so that every interval gets
        roughly 500 bars in view — so a <K>1D</K> pane sitting beside a <K>1m</K> pane in 2-split layout still has
        a full chart&apos;s worth of candles. Formula (for NSE, 375 trading minutes/day):
        <Box component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.74rem', bgcolor: 'action.hover', p: 1, mt: 0.5, borderRadius: 0.5, overflow: 'auto' }}>
{`barsPerCalDay  = (interval < 375) ? (375 / interval) : (1 / ceil(interval / 375))
daysBack       = max(7, ceil(500 / barsPerCalDay × 1.5))
fromSec        = now − daysBack × 86400`}
        </Box>
        For Delta: <K>barsPerCalDay = 1440 / interval</K> (24×7, no calendar overhead).
      </Section>

      <Section title="Storage model — stored or live-pulled?">
        <strong>Not stored.</strong> The candle database is <em>not</em> a persisted chart cache. Every pane render goes
        to the broker API via the backend, subject to the provider&apos;s own rate limits. To avoid hitting those limits,
        the page loads panes <em>sequentially</em> with a 350 ms gap between calls, and live updates poll every 15 s.
        <br /><br />
        <strong>Strategy scans</strong> (Strategies page) share a short-lived in-memory candle cache on the backend
        so that scanning 50 symbols doesn&apos;t fire 50 parallel Zerodha calls. Chart views do not share that cache —
        they always fetch fresh.
      </Section>

      <Section title="Non-standard intervals (45m, 90m, 4H, 1W)">
        Zerodha does not return 45-minute, 90-minute, 4-hour or 1-week bars natively. The backend fetches the next
        smaller native bar (typically 15 m, or 1 day for the weekly case) and aggregates in memory:
        <Box component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.74rem', bgcolor: 'action.hover', p: 1, mt: 0.5, borderRadius: 0.5, overflow: 'auto' }}>
{`45m  ← 15-minute bars  (3 per bar)
90m  ← 15-minute bars  (6 per bar)
4H   ← 15-minute bars  (16 per bar)
1W   ← 1-day bars      (5 per bar, Mon–Fri)`}
        </Box>
        Buckets are aligned to the <strong>market-open offset</strong> for the source exchange:
        NSE/BSE/NFO <K>09:15 IST</K>, MCX/CDS/BFO <K>09:00 IST</K>, Delta <K>00:00 UTC</K>. That way a 45-minute bar
        always starts at an exchange-logical moment (e.g. 09:15, 10:00, 10:45…) rather than at the top of the UTC hour.
        <br /><br />
        Aggregation uses standard OHLC rules: <K>open = first</K>, <K>high = max</K>, <K>low = min</K>,
        <K>close = last</K>, <K>volume = sum</K>.
      </Section>

      <Section title="Layout modes">
        <strong>Single</strong> — one full-size chart.<br />
        <strong>2-Split</strong> — two charts side-by-side with <em>independent</em> timeframes (e.g., 15 m + 1 H).
        Crosshair is synced across panes.<br />
        <strong>4-Quad (Options)</strong> — Q1 Spot, Q2 CE, Q3 Combined CE+PE candlestick (rising combined price
        signals a big directional move beating theta decay), Q4 PE. Strike/expiry independent for CE and PE.<br />
        <strong>Options Workspace</strong> — Delta BTC/ETH: live option chain (click Bid = sell, Ask = buy, or hover +
        S/B), basket builder with ready-made strategies, payoff / P&amp;L table / heatmap / Greeks with price, date and IV
        what-if sliders, and the spot chart as a resizable strip (click a Mark to chart that option instead). Places PAPER
        baskets with an optional fixed ₹ target/stop; open paper baskets can be rolled, adjusted and closed, and a
        strangle playbook card tracks the premium ratio and suggests rolls. Full guide: App Guide → Options Workspace.
      </Section>

      <Section title="Candle types (Regular / HA / CSH / CSL / CLH / CLN)">
        <strong>Regular</strong> — raw OHLC.<br />
        <strong>Heikin-Ashi (HA)</strong> — smoothed candles: <K>haClose = (O+H+L+C)/4</K>,
        <K>haOpen = (haOpen[prev] + haClose[prev]) / 2</K>.<br />
        <strong>Custom Smooth HA (CSH)</strong> — HA fed into a light LR smoother.<br />
        <strong>Custom Smooth Linreg (CSL)</strong> — OHLC each passed through a 14-length linear regression.<br />
        <strong>Custom Linreg + HA (CLH)</strong> — LR-smoothed OHLC then HA on top. Matches the internal DailyBlast candles.<br />
        <strong>Candle + Linear regressioN (CLN)</strong> — the only <em>composite</em> type: it draws the{' '}
        <em>real</em> market candle and a wickless linear-regression body on top of it, at the same time.
        The regression body is <K>linreg(open, N)</K> → <K>linreg(close, N)</K> (N configurable in Chart
        Settings → Candles, default <K>14</K>); it is body-only by design, because regressing the high and
        low separately produces excursions that mean nothing.<br />
        <strong>Colour is decided by the regression, not by the bar.</strong> Both layers take the LR
        direction, so a red bar inside a rising regression prints bullish — that disagreement (a big real
        candle with a small opposite-direction regression body) is the thing the type exists to show.
        Because that is a hard rule, an active strategy's candle colours are suppressed while CLN is
        selected; its markers, signals and tooltips are unaffected. The <K>Wick</K> toggle governs the real
        candle only — the regression layer never has wicks in any state. Unlike every other type, CLN does
        not change the bar geometry: indicators, the crosshair, the OHLC legend and the LTP line all keep
        reading true prices, and scans/backtests treat CLN exactly as Regular.<br />
        <strong>LR chip</strong> — draws the same regression body over <em>any</em> candle type (Candle group).
        Candles are ghosted under it by default (Chart Settings → LR body (CLN) → "ghost the candles"); CLN is
        Regular + LR + ghost, so switching LR off while on CLN returns to Regular.
      </Section>

      <Section title="Indicators — classic overlays">
        <Chip label="BB" size="small" sx={{ mr: 0.5, fontSize: '0.7rem' }} />
        Bollinger Bands (length <K>20</K>, multiplier <K>2σ</K>) computed on <K>close</K>. Upper = mid + 2σ, lower = mid − 2σ.
        <br />
        <Chip label="EMAs" size="small" sx={{ mr: 0.5, mt: 0.5, fontSize: '0.7rem' }} />
        Multi-period exponential moving averages — <K>EMA(7)</K> orange, <K>EMA(21)</K> amber,
        <K>EMA(51)</K> cyan, <K>EMA(200)</K> purple — all computed on the
        <K>LinReg(close, 14)</K> source. Each line uses a distinct color and progressively-thicker
        stroke so the four lines stay easy to distinguish during trends and crossovers.
        <br />
        <Chip label="FNO" size="small" color="secondary" sx={{ mr: 0.5, mt: 0.5, fontSize: '0.7rem' }} />
        Options-derived price levels: Max Pain, GEX-flip, supports/resistances from OI accumulation. Refreshes every 5 min.
      </Section>

      <Section title="Indicators — advanced (DailyBlast-derived)">
        All advanced overlays lazy-fetch the higher-timeframe candles they need (CTF for AvgCloud/CMid/CPR/C-POC; HTF for H-POC).
        CTF is mapped from your pane&apos;s interval (e.g. 15 m → 75 m, 1 H → 5 H, 1 D → 1 W). HTF = CTF × 15.
        <br /><br />
        <Chip label="AvgCloud" size="small" color="info" sx={{ mr: 0.5, fontSize: '0.7rem' }} />
        <strong>Avg Band ↔ Combined Midline cloud.</strong> Mirrors Pine&apos;s{' '}
        <em>Show Cloud: Avg Band ↔ Combined Mid</em>. With <K>avg_top, avg_bot</K> from the LR_HAMA_Band
        (Avg of SLR and HAMA open/close clouds) and <K>mid</K> = Combined Midline, the cloud fills
        between the avg band&apos;s near edge and the midline:
        <K>edge = (avg_top+avg_bot)/2 &gt; mid ? avg_bot : avg_top</K>. Colour is{' '}
        <em>Bullish</em> when the avg-band centre is above the midline, <em>Bearish</em> below,{' '}
        <em>Neutral</em> equal. Colours & opacity in Chart Settings → Bands.
        <br /><br />
        <Chip label="CMid" size="small" color="info" sx={{ mr: 0.5, fontSize: '0.7rem' }} />
        <strong>Combined Midline</strong> — <K>(combined_upper + combined_lower) / 2</K>, dashed.
        <br /><br />
        <Chip label="CPR" size="small" color="info" sx={{ mr: 0.5, fontSize: '0.7rem' }} />
        <strong>Central Pivot Range</strong> computed from the <em>previous</em> CTF period&apos;s H/L/C.
        <K>Pivot = (H+L+C)/3</K>, <K>BC = (H+L)/2</K>, <K>TC = Pivot + (Pivot − BC)</K>. The period
        base is derived from the pane&apos;s interval per Pine parity:
        <K>≤1m → 60m</K>, <K>≤5m → 240m</K>, <K>≤15m → D</K>, <K>≤240m → W</K>, <K>&gt;240m → 2W</K>.
        Period boundaries align to the exchange&apos;s session open — <K>09:15 IST</K> for NSE/NFO/BFO,
        <K>09:00 IST</K> for MCX, <K>05:30 IST</K> for Delta — not to UTC midnight, so an intraday CPR
        never splits in the middle of a trading day.
        Pivot midline defaults to OFF; enable via <em>Chart Settings → Band fills → Show CPR midline</em>.
        TC↔BC fill opacity is also user-tunable.
        <br /><br />
        <Chip label="C-POC / H-POC" size="small" color="info" sx={{ mr: 0.5, fontSize: '0.7rem' }} />
        <strong>Point-of-Control band.</strong> Volume profile with <K>25</K> rows across the CPR-window&apos;s
        high-low range. Each bar&apos;s volume is distributed across rows it touches. The argmax row is the POC;
        its row-band is rendered as a translucent horizontal band. C-POC uses CTF bars; H-POC uses HTF bars.
        <br /><br />
        <Chip label="BBR" size="small" color="info" sx={{ mr: 0.5, fontSize: '0.7rem' }} />
        <strong>BBR100 extreme-reversal candle color.</strong> BBR100 rescales BB to [−100, +100]:
        <K>BBR = ((HLC3 − lower) / (upper − lower)) × 200 − 100</K>. A candle is flagged <em>only</em> when BBR
        is in or just came from the extreme zone <em>and</em> is reversing: (overbought) <K>bbr ≥ +100 or prev ≥ +100 AND bbr &lt; prev</K>;
        (oversold) <K>bbr ≤ −100 or prev ≤ −100 AND bbr &gt; prev</K>. <strong>Highest priority</strong> —
        overrides strategy colors and envelope-touch colors.
        <br /><br />
        <Chip label="VSpk" size="small" color="info" sx={{ mr: 0.5, fontSize: '0.7rem' }} />
        <strong>Volume-spike candle color.</strong> <K>volume &gt; SMA(volume, 20) + 2·σ(volume, 20)</K>. Highest priority.
      </Section>

      <Section title="Candle-color priority">
        <strong>1.</strong> BBR reversal <em>or</em> volume spike (if either toggle is on).<br />
        <strong>2.</strong> Strategy-supplied colors (RockBand state colors, DailyBlast state colors, etc.).<br />
        <strong>3.</strong> Default theme up/down colors.
      </Section>

      <Section title="Persistence & themes">
        Per-symbol preferences — layout mode, both intervals, every overlay toggle, strategy selection, CE/PE strike and expiry —
        are auto-saved to <K>/charts/preferences/:symbol</K> (1.5 s debounce after the last change). When you revisit a symbol,
        preferences are hydrated before any candles are fetched. Works across devices.
        <br /><br />
        All indicator palettes are theme-aware: darker saturated colors in light mode, lighter pastel colors in dark mode.
        Translucent fills (envelopes, POC bands) keep the underlying candles readable in both themes.
      </Section>

      <Section title="Strategy overlays vs independent indicators">
        <strong>Strategy overlays</strong> (Strategy dropdown → RockBand / DailyBlast) are the strategy&apos;s own visual layer:
        per-candle colors encoding its current state, markers for BUY/SHORT signals, optional trigger lines.
        <br /><br />
        <strong>Independent indicators</strong> (chips: BB, EMAs, FNO, AvgCloud, CMid, CPR, C-POC, H-POC, BBR, VSpk, LR_HAMA_Band,
        AV_Band, Flow/Vol, …) are chartable regardless of whether a strategy is selected — useful when you want to study raw
        structure without a strategy&apos;s lens.
      </Section>

      <Section title="Flow / Vol chip (one button, cycles)">
        A single chip cycles the two lower-pane volume views. On Delta it goes <strong>Off → Flow → Vol → Off</strong>; on other
        exchanges (no Flow) it is <strong>Off → Vol → Off</strong>. <strong>Flow</strong> is the historical Order-Flow ribbon
        (taker buy/sell bias). <strong>Vol</strong> is <K>Weighted Volume = volume × close</K>: LinReg(7)-smoothed, with
        <K>Fast EMA(3)</K> / <K>Slow EMA(21)</K> and a bull/bear band between them, plus <strong>spike bars</strong> (raw wVol
        &gt; Slow × 2). Toggle the EMAs and pick band/spike colours in Chart Settings → Indicators → Vol.<br /><br />
        <strong>R123 — Vol also feeds the OverView score</strong> as a latched constituent: a GREEN spike latches the
        Vol vote bullish until the next RED spike (and vice-versa). The pane and the voter compute on the same raw
        series, so a green spike in the pane lines up with the bar colour the voter produces. Turn it on/off in
        Chart Settings → Indicators → OverView → Contributors.
      </Section>

      <Section title="AV_Band — Anchored Volume Band">
        From WVol spike bars it holds a High/Low zone; an <strong>Effective Line</strong> picks the zone&apos;s upper / lower / mid
        edge by this candle&apos;s chart-TF OverView score. Two selectable rules: <strong>Logic 1</strong> (default) — <K>&gt;20</K> →
        high, <K>&lt;-20</K> → low, else mid; <strong>Logic 2</strong> — inverted (<K>&gt;20</K> → low, <K>&lt;-20</K> → high, else
        mid). Two HMAs of that line — <strong>fast</strong> (default 14) and <strong>slow</strong> (default 100) — form a band on
        the price pane, bullish when the fast HMA ≥ slow HMA. In Chart Settings → Indicators → AV_Band you can choose the
        rule, the line style (<strong>staircase</strong> or <strong>smooth line</strong>), set periods / line width /
        Effective-Line thickness, toggle Show Fast / Slow HMA / Band / Effective Line, and set band colours + opacity.<br /><br />
        <strong>R123 — AV_Band also feeds the OverView score</strong> as a latched constituent: <K>+1</K> when close &gt;
        the Fast HMA, <K>−1</K> when close &lt; it. The voter uses the SAME Effective-Line rule (Logic 1/2) and Fast-HMA
        period you set here and is computed on the raw series, so it agrees with the drawn band. To break the
        circular dependency (the Effective Line is derived from the OverView score), the voter resolves its Effective
        Line from the score <em>excluding</em> AV_Band itself. Turn it on/off in OverView → Contributors.
      </Section>

      <Section title="VOB — Volume-Trend Order Block">
        A custom <strong>supertrend</strong> (<K>hl2 ± multiplier · SMA(high−low, len)</K>) draws a trailing-stop line with a
        directional <strong>cloud</strong> between price and the line — teal in an up-trend, pink in a down-trend; the trend
        flips when close crosses the prior stop. On a confirmed <strong>swing pivot</strong> (Pivot Strength bars each side)
        in the matching trend it plots a <strong>volume-split order block</strong>: a zone split into a top <strong>Sell %</strong>{' '}
        half and a bottom <strong>Buy %</strong> half by the buy/sell volume ratio over the pivot window (bullish teal,
        bearish pink; the dominant half is shaded stronger). A new block can&apos;t overlap the active one; the active block
        extends right and — when <strong>Delete on break</strong> is on — is removed once price fully breaks through it.<br /><br />
        <strong>Retest crosses</strong> mark price crossing back above a bull OB top (when its Buy % ≥ your Min Buy %) or below
        a bear OB bottom (Sell % ≥ Min Sell %). Everything is computed on <strong>closed raw candles</strong> (repaint-safe;
        blocks confirm Pivot-Strength bars after the swing) so it&apos;s identical across display candle types. Toggle via the
        <strong> VOB</strong> chip after AV_Band; set the SMA length / multiplier / cloud colours, pivot strength, OB &amp;
        label colours, delete-on-break and the retest % filters in Chart Settings → VOB.
      </Section>

      <Section title="FNO — Options levels (GEX · Max Pain · OI walls)">
        For <strong>BTC &amp; ETH</strong> (the liquid Delta options), the <strong>FNO</strong> chip overlays option-derived
        levels as <strong>running lines</strong> per expiry tier — <strong>Day 0</strong> (nearest daily, solid),{' '}
        <strong>Week 0</strong> (weekly, dashed), <strong>Month 0</strong> (monthly, dotted), each selectable in Chart Settings → FNO:
        <br /><br />
        • <strong>OI-wall Support/Resistance</strong> — strikes with the largest put-OI (support) / call-OI (resistance).<br />
        • <strong>Max Pain</strong> — the strike where option buyers lose the most (price often gravitates here into expiry).<br />
        • <strong>GEX flip (zero-gamma)</strong> — the level separating the positive- and negative-gamma regimes.<br />
        • <strong>GEX walls</strong> — the current top ±3 gamma strikes: <K>green</K> = positive/long-gamma, <K>red</K> = negative/short-gamma.<br /><br />
        <strong>How to trade it (gamma regime).</strong> <strong>GEX = Σ (γ<sub>call</sub>·OI − γ<sub>put</sub>·OI)·lot·spot²·0.01</strong>.
        When net gamma is <strong>positive (long gamma)</strong>, dealers hedge AGAINST moves → price tends to pin / mean-revert
        between the walls — favour <strong>selling straddles/strangles</strong> and ITM credit spreads. When net gamma is{' '}
        <strong>negative (short gamma)</strong>, dealer hedging AMPLIFIES moves → expect trends/breakouts — favour{' '}
        <strong>debit strategies / spreads</strong> in the breakout direction. The on-chart panel shows the live regime
        (LONG γ / SHORT γ) and net GEX. Levels are non-repainting and accumulate history from when capture began.
        (Delta options are captured BTC/ETH only — NSE direct polling stays off.)
      </Section>

      <Section title="Horizontal levels — use HVN">
        The standalone &quot;Line&quot; drawing tool was removed; the <strong>HVN</strong> chip provides the same place / drag /
        delete horizontal levels (click HVN, then click the chart to drop a level; drag the right-axis handle to move, × to delete).
        The chip is <strong>one-shot</strong> — it disarms after a single drop, so click it again for the next level.<br /><br />
        Each HVN also appears on the Dashboard&apos;s <strong>Price Level Watch</strong> and raises a <strong>cross alert
        (+ Telegram)</strong> when price touches it — it is alert-only and never places an order. HVNs additionally feed the
        Order-Flow absorption detector.
      </Section>

      <Section title="Symbol panel & navigation">
        Type 2+ characters to search all instruments. Exchange chips filter the result (<em>Tracked</em> shows your
        starred symbols; <em>Browsed</em> shows your recent chart history). Use <strong>↑</strong>/<strong>↓</strong> to
        step through the current (sorted, filtered) symbol list — it works regardless of focus, except while you're
        typing in a field or a dialog is open. The last viewed symbol is remembered across page navigations.
      </Section>

      <Section title="Live updates & countdown">
        While a chart is open, the latest bar is polled on a user-configurable cadence
        (<em>Chart Settings → Live update</em>, default <K>15s</K>, minimum <K>5s</K>) and patched into the
        existing series via <K>candleSeries.update()</K> — no full redraw. New bars that close during the
        session are merged into the pane&apos;s history so the per-layout candle cap tail view stays correct.
        A <K>mm:ss</K> pill on the right axis counts down to the next bar close; for daily/weekly bars it
        shows <K>Hh Mm</K>. The countdown self-heals if the feed is paused — it rolls forward to the next
        aligned boundary past <K>now</K> rather than freezing at 00:00.
      </Section>

      <Section title="Chart Settings (gear icon)">
        A per-user Chart Settings store lets you save any number of templates and activate one at a time.
        The currently active template drives <em>every</em> chart in every layout and every interval.
        Fields:
        <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
          <li><strong>Candle colors</strong> — rising/falling fill; toggle whether borders and wicks match the fill.</li>
          <li><strong>Remembered zoom (per layout)</strong> — each layout (single / 2-split / 4-quad) now OPENS at the
            candle count you last zoomed to. Just scroll-zoom to the number of candles you like; every new symbol you
            open in that layout uses that zoom. It's stored per device, separately for each layout. A
            <K>Reset remembered zoom</K> button in Chart Settings restores the defaults (Single 300, 2-Split 150,
            4-Quad 130). Older bars stay loaded for indicators and lazy-load-more.</li>
          <li><strong>Live update cadence</strong> — polling interval in seconds.</li>
          <li><strong>Band fills</strong> — opacity of CPR TC↔BC, POC, and Combined Envelope translucent fills;
            toggle for the CPR pivot midline (default OFF).</li>
          <li><strong>BBR / VSpk colors</strong> — the candle-color overrides used by BBR reversal and volume-spike
            indicators.</li>
          <li><strong>LTP line</strong> — color and width of the right-axis Last Traded Price line.</li>
          <li><strong>Session lines</strong> — vertical dashed/dotted/solid markers at session opens
            (<K>09:15 IST</K> NSE / <K>09:00 IST</K> MCX / <K>05:30 IST</K> Delta), toggle + color + opacity.</li>
          <li><strong>Grid opacity</strong> — chart background grid lines.</li>
        </Box>
      </Section>

      <Section title="Keyboard & mouse tips">
        • Scroll to zoom, drag to pan.<br />
        • Right-click the price scale to auto-fit.<br />
        • Drag the round chip on the right axis to move a trigger level (NBUY/NSHORT/BBUY/BSHORT).
          FNO-derived levels (MaxPain, GEX Flip, R1/R2, S1/S2) are read-only — no drag handle appears for them.<br />
        • On Delta BTCUSD / ETHUSD, the <K>⚡</K> chip next to a set level attaches an option strategy that opens
          (LIVE by default, or PAPER) when the level fires — only while the Dashboard's Auto-Execute is ON.<br />
        • Pan past the leftmost bar to lazy-load 300 more historical bars.<br />
        • When you pan away from the tail, a floating <K>»</K> chevron appears in the bottom-right — click it to
          snap back to the latest candle (uses <K>scrollToRealTime()</K>).<br />
        • Five empty candle slots on the right let you see the latest bar without clipping.
      </Section>

      <Section title="Order placement — BUY / SHORT & multi-entry scale-in">
        <strong>Capture flow.</strong> Click the green <strong>BUY</strong> or red <strong>SHORT</strong> button, then
        click the chart to set your level(s). The order is: each Entry → Stop-Loss → Target. A confirm dialog then
        shows the auto-computed quantity (editable), the order type for Entry 1 (LIMIT rests / MARKET fires now),
        and the Risk:Reward.<br /><br />
        <strong>Multi-entry scale-in (averaging-down).</strong> Set <strong>Settings → Trading Preferences →
        Entry Splitting (N)</strong> to 1–5 (default 3). With N&gt;1 you pick N entry levels and the total quantity
        is split by the <em>doubling rule</em> so the last leg is half the size and each fill recenters your blended
        average to the midpoint:<br />
        &nbsp;&nbsp;• N=2 → ½, ½ &nbsp; • N=3 → ¼, ¼, ½ &nbsp; • N=4 → ⅛, ⅛, ¼, ½ &nbsp; • N=5 → 1/16, 1/16, ⅛, ¼, ½<br /><br />
        <strong>Ordering.</strong> For BUY each entry must be <em>below</em> the previous (E1 &gt; E2 &gt; …), with the
        SL below the last entry and Target above E1. For SHORT it's mirrored. The confirm dialog's{' '}
        <em>"If filled through each level"</em> table shows the loss-if-SL and profit-if-target at every stage.<br /><br />
        <strong>Execution &amp; exits.</strong> Each entry is monitored locally (the exchange never sees the levels) and
        fires a MARKET order when its price is reached, averaging into one position. The ISL is active from the first
        fill. If Target is hit at any stage the whole position exits and the remaining un-fired entries are cancelled.
        If price gaps to the SL the filled quantity is closed and the rest cancelled. SL-breach confirmation timing
        comes from <strong>Settings → Trailing SL</strong>.<br /><br />
        <strong>On the chart &amp; dashboard.</strong> Every entry level renders as a draggable dashed line labelled
        E1/E2/…, and the shared <strong>SL</strong> and <strong>Target</strong> render as draggable lines too —
        visible even before any entry fires. Drag any of them to adjust; the change is saved to the pending
        entries and reflected in the dashboard within one poll. The Dashboard's <strong>Pending Entry Watch</strong>{' '}
        shows each scale-in as a <strong>collapsible group</strong> — click the header (side · filled/total · SL ·
        Target) to expand the individual legs, each with an E{'{'}index{'}'}/{'{'}N{'}'} badge and PENDING /
        TRIGGERED status. The header has a chart shortcut and a remove button: with no fills it cancels all pending
        entries (and their SL/Target); once a leg has filled it cancels only the remaining legs and leaves the open
        position's SL/Target intact. Changing N later never re-shapes an already-placed group.
      </Section>

      <Section title="OverView trend meter & Bkground tint">
        <strong>OverView</strong> (toggle before BB) pins a compact dashboard to the chart's top-left with two
        half-circle speedometers — one for the chart timeframe and one for its mapped Higher Timeframe (from
        Strategies → Timeframe Mapping). Each gauge's needle is a consolidated <strong>-100..+100</strong> score; under
        it, the constituents are listed in a two-column grid, each showing a colored shape (▲ up · ▼ down · ◆ neutral ·
        – no data). A constituent that's switched OFF is shown dimmed + struck-through. Hover any shape for the exact
        reason.<br /><br />
        <strong>The 14 constituents (R123).</strong> EMAs, AvgCloud, CPR, C-POC, H-POC, RelStr, ChgN%, MACD, JHM, WR%,
        and four added in R123 — <strong>BO_D</strong>, <strong>BO_W</strong>, <strong>Vol</strong> and{' '}
        <strong>AV_Band</strong>.<br />
        <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
          <li><strong>Per-bar (instant) voters:</strong> EMAs (close above both EMA#3 &amp; #4 = up), AvgCloud / CPR /
            C-POC / H-POC (above band = up, below = down, inside = neutral), RelStr &amp; ChgN% (line above its EMA = up),
            MACD (line rising = up, on CSH close), JHM (EMA-of-RSI above VWMA-of-RSI = up), WR% (BUY/SHORT zone).</li>
          <li><strong>Latched (stateful) voters:</strong> <strong>BO_D / BO_W</strong> — close ≥ the Daily/Weekly BUY
            line latches <K>+1</K> and holds until close ≤ the SHORT line latches <K>−1</K> (and vice-versa).
            <strong> Vol</strong> — a GREEN weighted-volume spike latches <K>+1</K>, a RED spike latches <K>−1</K>, held
            in between. <strong>AV_Band</strong> — close vs the Fast HMA of the Effective Line (uses the same Logic 1/2
            rule you pick for the drawn AV_Band, so the voter and the band agree).</li>
        </Box>
        <strong>Score</strong> = the <em>weighted</em> average of the ON constituents that have a signal (±1 each),
        scaled to ±100. Zones: Strong Buy ≥ +60, Probable Buy +20..+60, Neutral ±20, Probable Short −20..−60,
        Strong Short ≤ −60.<br /><br />
        <strong>Turn constituents on/off &amp; weight them.</strong> In <strong>Chart Settings → Indicators →
        OverView → Contributors</strong> each of the 14 has an on/off switch (off = weight 0 = excluded from the score
        everywhere), plus a numeric weight for fine-tuning the relative pull of the ON ones. Zone colors, shape colors
        and thresholds are editable in the same place. <em>Tip:</em> turn ON just one constituent to see exactly how it
        colors the bars (with RockBand active) — its up/down where it has a signal, neutral grey where it doesn't.<br /><br />
        <strong>Candle-type independent.</strong> Every constituent is computed on the <em>raw</em> OHLC series (not the
        display candle type), and CPR/POC use the app-standard base period (≤15m → Daily, ≤4h → Weekly, &gt;4h →
        Monthly). This is exactly what the Scan, Backtest and Auto-Trade use, so the chart's OverView matches them
        bar-for-bar.<br /><br />
        <strong>What it drives.</strong> With the <strong>RockBand</strong> strategy active the score becomes each
        candle's regime colour (chart-TF zone) and, together with the HTF zone, the BUY/SHORT signals — the SAME engine
        the Scan, Backtest and autonomous Auto-Trade run. So toggling a constituent (or changing its weight / the
        CPR/POC period) shifts the live signals; re-run a Backtest after such a change.<br /><br />
        <strong>Bkground</strong> (toggle after OverView) tints the whole chart with the trend color of the
        <em> blended</em> chart-TF + HTF score, so it only leans green/red when both timeframes agree. Neutral leaves
        the chart untinted. Tune its strength with the Bkground tint opacity in Chart Settings.<br /><br />
        <strong>Crosshair tooltip.</strong> With OverView on, moving the crosshair over any candle pops a small
        chip with that candle's OverView — the score + zone for both the chart timeframe and its HTF, plus the
        up/down counts.
      </Section>

      <Section title="Chart loading — last-used setup">
        Opening any symbol restores the indicators, timeframe and layout you last used on <K>any</K> symbol
        (remembered automatically — there's no "On click load" switch anymore). On the very first load, a default
        set is on: OverView, Bkground, AvgCloud, C-POC, LR_HAMA_Band, RelStrength, ChgN% and MACD.
      </Section>

    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} size="small">Close</Button>
    </DialogActions>
  </Dialog>
);

export default ChartsHelpDialog;
